import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useParams } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import axios from "axios";
import { 
  MapPin, Phone, Mail, Star, ChevronDown, Check, Users, 
  Clock, Mountain, Compass, Shield, Calendar as CalendarIcon,
  ArrowRight, Menu, X, Send, Timer, Globe
} from "lucide-react";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import { Toaster, toast } from "sonner";
import { format } from "date-fns";
import AdminPanel from "./AdminPanel";
import ArticlesList from "./pages/Articles";
import ArticlePage from "./pages/ArticlePage";
import ThankYou from "./pages/ThankYou";
import Terms from "./pages/Terms";
import TourProductPage from "./pages/TourProductPage";
import SignAgreement from "./pages/SignAgreement";
import { trackEvent, trackBookingStarted, trackBookingStep } from "./hooks/useAnalytics";

// Resolve backend origin from env with window.location.origin fallback for
// production builds that don't bake in REACT_APP_BACKEND_URL.
const RUNTIME_ORIGIN = (() => {
  const env = process.env.REACT_APP_BACKEND_URL;
  if (env && typeof env === "string" && env.startsWith("http")) return env.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location && window.location.origin) return window.location.origin;
  return "";
})();
const BACKEND_URL = RUNTIME_ORIGIN;
const API = `${BACKEND_URL}/api`;

// Resolve image URLs — supports /api/media/... (admin uploaded), Dropbox conversion, absolute, /images/...
const resolveImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.includes('dropbox.com')) {
    // Convert sharing URL to direct hotlink-able URL
    let converted = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    converted = converted.replace(/[&?]dl=0/, '');
    return converted;
  }
  return url;
};

// Images
const IMAGES = {
  logoVertical: "/images/logo_ar.webp",
  logoHorizontal: "/images/logo_ar.webp",
  hero: "/images/annons.webp",
  bike1: "/images/IMG_3641.webp",
  bikesLandscape: "/images/rider_action.webp",
  kimmo: "/images/kimmo.webp",
  purre: "/images/purre.webp",
  gallery: [
    "/images/IMG_3641.webp",
    "/images/IMG_3614.webp",
    "/images/rider_action.webp",
    "/images/IMG_3745.webp",
    "/images/IMG_3652.webp",
    "/images/purre.webp"
  ]
};

// Site config — Sweden only
const SITE_CONFIG = {
  id: 'sweden',
  name: 'Arctic Riders',
  currency: 'kr',
  currencyCode: 'SEK',
  phone: '+46 70 744 5405',
  phoneRaw: '+46707445405',
  email: 'info@arcticriders.com',
  address: 'Torpvägen 20, 147 63 Uttran',
  hero: {
    title: 'HYR ÄVENTYRSMOTORCYKEL',
    titleEn: 'RENT ADVENTURE MOTORCYCLES',
    subtitle: 'I STOCKHOLM',
    subtitleEn: 'IN STOCKHOLM',
    description: 'Upplev Stockholms skärgård, skogar och grusvägar på en CF Moto 450 MT. Halvdag eller heldag — du bestämmer.',
    descriptionEn: 'Explore the Stockholm archipelago, forests, and gravel roads on a CF Moto 450 MT. Half day or full day — you decide.'
  },
  seo: {
    title: 'Hyr Adventure Motorcykel i Stockholm | Arctic Riders',
    titleEn: 'Rent Adventure Motorcycle in Stockholm | Arctic Riders',
    description: 'Hyr äventyrsmotorcykel CF Moto 450 MT i Stockholm. Utforska skärgården och grusvägar. Halvdag från 1 990 kr.',
    descriptionEn: 'Rent CF Moto 450 MT adventure motorcycle in Stockholm. Explore the archipelago and gravel roads. Half day from 1,990 SEK.'
  },
  packages: {
    half_day: { 
      id: "half_day",
      name: "Halvdagsuthyrning",
      nameEn: "Half Day Rental", 
      price: 1990,
      description: "Självguidad tur runt Stockholm — 5 timmar",
      descriptionEn: "Self-guided adventure around Stockholm — 5 hours",
      timeSlots: "09:00-14:00 eller 14:00-19:00",
      timeSlotsEn: "09:00-14:00 or 14:00-19:00",
      features: ["5 timmars uthyrning", "09:00-14:00 eller 14:00-19:00", "CF Moto 450MT + hjälm & utrustning", "Minst 20 år, MC-körkort krävs"],
      featuresEn: ["5 hour rental", "09:00-14:00 or 14:00-19:00", "CF Moto 450MT + helmet & gear", "Min. age 20, motorcycle license required"],
      highlight: false,
      multiDay: false
    },
    full_day: { 
      id: "full_day",
      name: "Heldagsuthyrning",
      nameEn: "Full Day Rental", 
      price: 3490,
      description: "Full frihet att utforska Stockholm och omnejd",
      descriptionEn: "Full freedom to explore Stockholm and beyond",
      timeSlots: "09:00-18:00",
      features: ["Heldagsuthyrning (09:00-18:00)", "CF Moto 450MT + hjälm & utrustning", "Kör i din egen takt", "2 dagar 6 490 kr · 3 dagar 8 990 kr"],
      featuresEn: ["Full day rental (09:00-18:00)", "CF Moto 450MT + helmet & gear", "Go at your own pace", "2 days 6,490 SEK · 3 days 8,990 SEK"],
      highlight: true,
      multiDay: true,
      multiDayPrices: { 1: 3490, 2: 6490, 3: 8990 },
      extraDayPrice: 2500
    }
  },
  guides: [
    {
      name: "Kimmo Ilvonen",
      role: "Grundare & Guide",
      roleEn: "Founder & Guide",
      image: "/images/kimmo.webp",
      bio: "Kört motorcykel i över 35 år. Kört i Afrika, Island, Finland, Estland, Frankrike och Spanien. Tävlat enduro på elitnivå. Grundare av Arctic Riders.",
      bioEn: "35+ years on motorcycles. Ridden across Africa, Iceland, Finland, Estonia, France, and Spain. Competed in enduro at elite level. Founder of Arctic Riders."
    },
    {
      name: 'Daniel "Purre" Norén',
      role: "Guide",
      roleEn: "Guide",
      image: "/images/purre.webp",
      bio: "Stor passion för adventure-åkning. Purre lever och andas motorcykel och delar gärna med sig av sin kunskap och entusiasm på varje tur.",
      bioEn: "A true passion for adventure riding. Purre lives and breathes motorcycles and loves sharing his knowledge and enthusiasm on every ride."
    }
  ]
};

// Helper — default to Swedish
const getUserLanguage = () => {
  const override = localStorage.getItem('arctic_riders_lang');
  if (override === 'sv' || override === 'en') return override;
  return 'sv'; // Default Swedish
};

const setUserLanguage = (lang) => {
  localStorage.setItem('arctic_riders_lang', lang);
  window.dispatchEvent(new Event('langchange'));
};

// Helper for translations
const t = (lang, sv, en) => lang === 'en' ? en : sv;

// Compatibility shim — all legacy components reference these
const PACKAGES = SITE_CONFIG.packages;

// Navigation
const Navigation = ({ scrollToSection, openBooking }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState(getUserLanguage());

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    const handleLangChange = () => setLang(getUserLanguage());
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("langchange", handleLangChange);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("langchange", handleLangChange);
    };
  }, []);

  const navItems = lang === 'sv' ? [
    { label: "Uthyrning", id: "adventures" },
    { label: "Turer", id: "upcoming-tours" },
    { label: "Guider", id: "guides" },
    { label: "Motorcyklar", id: "bikes" },
    { label: "Galleri", id: "gallery" },
    { label: "FAQ", id: "faq" },
    { label: "Kontakt", id: "contact" }
  ] : [
    { label: "Rentals", id: "adventures" },
    { label: "Tours", id: "upcoming-tours" },
    { label: "Guides", id: "guides" },
    { label: "Bikes", id: "bikes" },
    { label: "Gallery", id: "gallery" },
    { label: "FAQ", id: "faq" },
    { label: "Contact", id: "contact" }
  ];

  const bookNowText = lang === 'sv' ? 'Boka Nu' : 'Book Now';

  const toggleLang = () => {
    const newLang = lang === 'sv' ? 'en' : 'sv';
    setUserLanguage(newLang);
    setLang(newLang);
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A0A0A]/95 backdrop-blur-md py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img 
            src={IMAGES.logoHorizontal} 
            alt="Arctic Riders" 
            className="h-10 sm:h-11 md:h-12"
            data-testid="logo"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="text-white/70 hover:text-white text-sm uppercase tracking-wider transition-colors font-manrope"
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs uppercase tracking-wider font-manrope border border-white/10 px-2.5 py-1.5 hover:border-white/30 transition-colors"
            data-testid="lang-toggle"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === 'sv' ? 'EN' : 'SV'}
          </button>
          <button 
            onClick={() => openBooking("half_day")}
            className="btn-primary text-sm py-3 px-6"
            data-testid="nav-book-now"
          >
            {bookNowText}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-toggle"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-[#0A0A0A]/98 backdrop-blur-md p-5 sm:p-6 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { scrollToSection(item.id); setIsOpen(false); }}
              className="block w-full text-left py-3 text-white/70 hover:text-white uppercase tracking-wider text-sm"
              data-testid={`mobile-nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <button 
            onClick={() => { openBooking("half_day"); setIsOpen(false); }}
            className="btn-primary w-full mt-3 text-sm py-3"
            data-testid="mobile-nav-book"
          >
            {bookNowText}
          </button>
          <button
            onClick={() => { toggleLang(); setIsOpen(false); }}
            className="flex items-center justify-center gap-2 w-full mt-3 text-white/70 hover:text-white text-sm uppercase tracking-wider font-manrope border border-white/10 px-2.5 py-3 hover:border-white/30 transition-colors"
            data-testid="mobile-lang-toggle"
          >
            <Globe className="w-4 h-4" />
            {lang === 'sv' ? 'Switch to English' : 'Byt till svenska'}
          </button>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = ({ scrollToSection, openBooking }) => {
  const lang = getUserLanguage();
  const hero = SITE_CONFIG.hero;

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${IMAGES.hero})` }}
      />
      <div className="absolute inset-0 overlay-gradient" />
      <div className="absolute inset-0 bg-black/30" />

      <div className="relative z-10 text-center px-5 sm:px-6 max-w-5xl mx-auto">
        <h1 
          className="font-anton text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white mb-4 sm:mb-6 animate-fade-up leading-tight"
          data-testid="hero-headline"
        >
          {t(lang, hero.title, hero.titleEn)}
          <br/><span className="text-[#EA580C]">{t(lang, hero.subtitle, hero.subtitleEn)}</span>
        </h1>
        <p 
          className="font-manrope text-base sm:text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed animate-fade-up delay-200"
          data-testid="hero-subtext"
        >
          {t(lang, hero.description, hero.descriptionEn)}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-fade-up delay-300">
          <button 
            onClick={() => openBooking("half_day")}
            className="btn-primary"
            data-testid="hero-book-bike"
          >
            {t(lang, 'Boka Halvdag', 'Book Half Day')}
          </button>
          <button 
            onClick={() => openBooking("full_day")}
            className="btn-secondary"
            data-testid="hero-book-fullday"
          >
            {t(lang, 'Boka Heldag', 'Book Full Day')}
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <button 
        onClick={() => scrollToSection("adventures")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 hover:text-white transition-colors animate-bounce"
        data-testid="scroll-indicator"
      >
        <ChevronDown size={32} />
      </button>
    </section>
  );
};

// About / Vår Story Section
const AboutSection = () => {
  const lang = getUserLanguage();

  return (
    <section
      id="about"
      className="section-padding bg-[#0A0A0A] grain-texture"
      data-testid="about-section"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
        <p className="font-manrope text-[#EA580C] text-xs sm:text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4" data-testid="about-eyebrow">
          {t(lang, 'Vår story', 'Our story')}
        </p>
        <h2 className="font-anton text-3xl sm:text-4xl md:text-5xl text-white mb-5 sm:mb-7 leading-tight" data-testid="about-headline">
          {t(
            lang,
            'Tjusningen med äventyrsmotorcykel',
            'The thrill of adventure motorcycling'
          )}
        </h2>
        <div className="font-manrope text-sm sm:text-base md:text-lg text-white/70 leading-relaxed space-y-4 sm:space-y-5" data-testid="about-body">
          <p>
            {t(
              lang,
              'Vi brinner för äventyr — och vi kan inte tänka oss ett bättre sätt att uppleva det än från sadeln på en motorcykel.',
              'We live for adventure — and we can\'t think of a better way to experience it than from the saddle of a motorcycle.'
            )}
          </p>
          <p>
            {t(
              lang,
              'Skogsvägar, grusvägar, kustvägar och tysta skärgårdsstigar runt Stockholm är vår lekplats. Det är där friheten finns — och det är därför vi startade Arctic Riders.',
              'Forest tracks, gravel roads, coastal routes, and quiet archipelago trails around Stockholm are our playground. That\'s where the freedom lives — and that\'s why we started Arctic Riders.'
            )}
          </p>
          <p className="text-white/90 font-medium">
            {t(
              lang,
              'Hos oss kan du antingen hyra din egen motorcykel och ge dig ut på egen hand — eller följa med oss på ett guidat äventyr.',
              'With us you can either rent your own motorcycle and head out solo — or join us on a fully guided adventure.'
            )}
          </p>
        </div>
      </div>
    </section>
  );
};

// Adventure Cards Section
const AdventuresSection = ({ openBooking }) => {
  const lang = getUserLanguage();
  const packages = SITE_CONFIG.packages;
  
  return (
    <section id="adventures" className="section-padding grain-texture" data-testid="adventures-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-anton text-3xl sm:text-4xl md:text-6xl text-white mb-3 sm:mb-4">
            {t(lang, 'Hyr Motorcykel', 'Rent a Motorcycle')}
          </h2>
          <p className="font-manrope text-sm sm:text-base text-white/60 max-w-xl mx-auto leading-relaxed">
            {t(lang, 'CF Moto 450 MT — hjälm och utrustning ingår. Välj halvdag eller heldag.', 'CF Moto 450 MT — helmet and gear included. Choose half day or full day.')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 sm:gap-8">
          {Object.values(packages).map((pkg, index) => {
            const pkgName = t(lang, pkg.name, pkg.nameEn);
            const pkgDesc = t(lang, pkg.description, pkg.descriptionEn);
            const pkgFeatures = t(lang, pkg.features, pkg.featuresEn);
            
            return (
              <div 
                key={pkg.id}
                className={`card-dark p-5 sm:p-8 flex flex-col relative overflow-hidden group ${
                  pkg.highlight ? "ring-2 ring-[#EA580C]" : ""
                }`}
                data-testid={`adventure-card-${pkg.id}`}
              >
                {pkg.highlight && (
                  <div className="absolute top-0 right-0 bg-[#EA580C] text-white text-xs uppercase tracking-wider py-1 px-4 font-manrope font-semibold">
                    {t(lang, 'Populär', 'Popular')}
                  </div>
                )}
                
                <h3 className="font-anton text-xl sm:text-2xl text-white mb-2">{pkgName}</h3>
                <p className="font-manrope text-white/60 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed">{pkgDesc}</p>
                
                <div className="mb-4 sm:mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="font-anton text-3xl sm:text-4xl text-[#EA580C]">
                      {pkg.price.toLocaleString('sv-SE')} kr
                    </span>
                  </div>
                  <span className="text-white/40 text-sm">/ {t(lang, 'förare', 'rider')}</span>
                </div>

                <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8 flex-grow">
                  {pkgFeatures.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5 sm:gap-3 text-white/70 text-xs sm:text-sm font-manrope leading-relaxed">
                      <Check className="w-4 h-4 text-[#EA580C] mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={() => openBooking(pkg.id)}
                  className={pkg.highlight ? "btn-primary w-full" : "btn-secondary w-full"}
                  data-testid={`book-${pkg.id}`}
                >
                  {t(lang, `Boka ${pkgName}`, `Book ${pkg.nameEn}`)}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Tour Experience Section
const TourSection = () => null; // Tours coming soon — managed via admin

// Bikes Section
const BikesSection = ({ openBooking }) => {
  const lang = getUserLanguage();
  const packages = SITE_CONFIG.packages;
  
  const halfDayPrice = `${packages.half_day.price.toLocaleString('sv-SE')} kr`;
  const fullDayPrice = `${packages.full_day.price.toLocaleString('sv-SE')} kr`;

  const bikeDescription = lang === 'sv'
    ? 'Äventyrsklar med utmärkt terrängförmåga. Perfekt för Sveriges varierande landskap — från mjuka motorvägar till skogsvägar och grusvägar.'
    : 'Adventure-ready with excellent off-road capability. Perfect for Sweden\'s varied terrain—from smooth highways to forest trails and gravel roads.';

  return (
    <section 
      id="bikes" 
      className="section-padding grain-texture"
      data-testid="bikes-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="font-anton text-3xl sm:text-4xl md:text-6xl text-white mb-3 sm:mb-4">
            {lang === 'sv' ? 'Våra Motorcyklar' : 'Our Bikes'}
          </h2>
          <p className="font-manrope text-sm sm:text-base text-white/60 max-w-xl mx-auto leading-relaxed">
            {lang === 'sv'
              ? 'Premium äventyrsmotorcyklar redo att erövra Sveriges varierande terräng.'
              : "Premium adventure motorcycles ready to conquer Sweden's diverse terrain."}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="relative overflow-hidden group">
            <img 
              src={IMAGES.bike1} 
              alt="CF Moto 450 MT" 
              className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105"
              data-testid="bike-image"
            />
          </div>
          
          <div className="card-dark p-5 sm:p-8">
            <h3 className="font-anton text-2xl sm:text-3xl text-white mb-2">CF Moto 450 MT</h3>
            <p className="font-manrope text-white/60 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed">
              {bikeDescription}
            </p>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-[#0A0A0A] p-3 sm:p-4 text-center">
                <div className="font-anton text-lg sm:text-2xl text-[#EA580C]">449cc</div>
                <div className="text-white/50 text-[10px] sm:text-xs uppercase tracking-wider mt-1">{lang === 'sv' ? 'Motor' : 'Engine'}</div>
              </div>
              <div className="bg-[#0A0A0A] p-3 sm:p-4 text-center">
                <div className="font-anton text-lg sm:text-2xl text-[#EA580C]">52 HP</div>
                <div className="text-white/50 text-[10px] sm:text-xs uppercase tracking-wider mt-1">{lang === 'sv' ? 'Effekt' : 'Power'}</div>
              </div>
              <div className="bg-[#0A0A0A] p-3 sm:p-4 text-center">
                <div className="font-anton text-lg sm:text-2xl text-[#EA580C]">208 kg</div>
                <div className="text-white/50 text-[10px] sm:text-xs uppercase tracking-wider mt-1">{lang === 'sv' ? 'Vikt' : 'Weight'}</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => openBooking("half_day")}
                className="btn-secondary flex-1"
                data-testid="bike-book-half"
              >
                {lang === 'sv' ? 'Halvdag' : 'Half Day'} — {halfDayPrice}
              </button>
              <button 
                onClick={() => openBooking("full_day")}
                className="btn-primary flex-1"
                data-testid="bike-book-full"
              >
                {lang === 'sv' ? 'Heldag' : 'Full Day'} — {fullDayPrice}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyUsSection = () => {
  const lang = getUserLanguage();

  const reasons = lang === 'sv' ? [
    { icon: MapPin, title: "Lokalkännedom", description: "Vi känner till varje väg och gömd pärla runt Stockholm." },
    { icon: Users, title: "Små Grupper", description: "Max 4 förare per tur för en personlig upplevelse." },
    { icon: Mountain, title: "Episka Rutter", description: "Noggrant utvalda vägar för det ultimata äventyret." },
    { icon: Shield, title: "Premium Motorcyklar", description: "Välunderhållna CF Moto 450 MT äventyrsmotorcyklar." }
  ] : [
    { icon: MapPin, title: "Local Expertise", description: "We know every road and hidden gem around Stockholm." },
    { icon: Users, title: "Small Groups", description: "Maximum 4 riders per tour for a personal experience." },
    { icon: Mountain, title: "Epic Routes", description: "Carefully selected roads for the ultimate adventure." },
    { icon: Shield, title: "Premium Bikes", description: "Well-maintained CF Moto 450 MT adventure motorcycles." }
  ];

  const subtitle = lang === 'sv' 
    ? 'Vi är själva motorcyklister och skapade Arctic Riders för att dela Sveriges skönhet på bästa möjliga sätt — från sadeln på en motorcykel.'
    : "We are riders ourselves and created Arctic Riders to share Sweden's beauty in the best possible way — from the seat of a motorcycle.";

  return (
    <section 
      id="whyus" 
      className="section-padding bg-[#171717] grain-texture"
      data-testid="why-us-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="font-anton text-3xl sm:text-4xl md:text-6xl text-white mb-3 sm:mb-4">
            {lang === 'sv' ? 'Varför Köra Med Arctic Riders' : 'Why Ride With Arctic Riders'}
          </h2>
          <p className="font-manrope text-sm sm:text-base text-white/60 max-w-2xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {reasons.map((reason, i) => (
            <div 
              key={i} 
              className="bg-[#0A0A0A] border border-white/5 p-4 sm:p-6 text-center group hover:border-[#EA580C]/50 transition-colors"
              data-testid={`reason-${i}`}
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#2e3104] mx-auto mb-3 sm:mb-4 flex items-center justify-center group-hover:bg-[#EA580C] transition-colors">
                <reason.icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#EA580C] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-anton text-base sm:text-xl text-white mb-1 sm:mb-2">{reason.title}</h3>
              <p className="font-manrope text-white/60 text-xs sm:text-sm leading-relaxed">{reason.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Guides Section
const GuidesSection = () => {
  const lang = getUserLanguage();
  
  return (
    <section id="guides" className="section-padding bg-[#171717] grain-texture" data-testid="guides-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="font-anton text-3xl sm:text-4xl md:text-6xl text-white mb-3 sm:mb-4">
            {t(lang, 'Våra Guider', 'Our Guides')}
          </h2>
          <p className="font-manrope text-sm sm:text-base text-white/60 max-w-xl mx-auto leading-relaxed">
            {t(lang, 'Erfarna förare med passion för äventyr.', 'Experienced riders with a passion for adventure.')}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 sm:gap-10">
          {SITE_CONFIG.guides.map((guide, i) => (
            <div key={i} className="bg-[#0A0A0A] border border-white/5 overflow-hidden group" data-testid={`guide-${i}`}>
              <div className="relative h-72 sm:h-80 overflow-hidden">
                <img src={guide.image} alt={guide.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5 sm:p-6">
                <h3 className="font-anton text-xl sm:text-2xl text-white mb-1">{guide.name}</h3>
                <p className="text-[#EA580C] font-manrope text-sm mb-3">{t(lang, guide.role, guide.roleEn)}</p>
                <p className="font-manrope text-white/60 text-sm leading-relaxed">{t(lang, guide.bio, guide.bioEn)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = () => {
  return (
    <section 
      id="reviews" 
      className="section-padding grain-texture"
      data-testid="reviews-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="font-anton text-3xl sm:text-4xl md:text-6xl text-white mb-4">
            What Riders Say
          </h2>
        </div>

        {/* Elfsight Google Reviews | Google Reviews Arctic Riders */}
        <div className="elfsight-app-8d1d72e0-5687-49d9-90a7-28cc67c61f19" data-elfsight-app-lazy></div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = ({ countryConfig }) => {
  const lang = getUserLanguage();
  const galleryImages = countryConfig?.images?.gallery || IMAGES.gallery;

  return (
    <section 
      id="gallery" 
      className="section-padding bg-[#171717] grain-texture"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="font-anton text-3xl sm:text-4xl md:text-6xl text-white mb-4">
            {lang === 'sv' ? 'Äventyret Väntar' : 'The Adventure Awaits'}
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
          {galleryImages.map((img, i) => (
            <div 
              key={i} 
              className={`overflow-hidden group ${i === 0 ? "col-span-2 row-span-2" : ""}`}
              data-testid={`gallery-image-${i}`}
            >
              <img 
                src={img} 
                alt={`Sweden adventure ${i + 1}`}
                className="w-full h-full object-cover aspect-square transition-transform duration-500 group-hover:scale-110"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = ({ countryConfig }) => {
  const lang = getUserLanguage();

  const faqs = lang === 'sv' ? [
    {
      question: "Vilken erfarenhetsnivå behöver jag?",
      answer: "Du bör ha ett giltigt motorcykelkörkort och vara bekväm med att köra på varierad terräng. Våra rutter inkluderar grusvägar, så viss off-road-erfarenhet är bra men inte nödvändigt."
    },
    {
      question: "Vad ingår i uthyrningen?",
      answer: "Alla uthyrningar inkluderar motorcykeln, hjälm och grundförsäkring. Vi ger en grundlig genomgång och ruttförslag. Bränsle ingår ej."
    },
    {
      question: "Vad ingår i den guidade turen?",
      answer: "Turen inkluderar din motorcykel, all säkerhetsutrustning, en erfaren guide, ruttplanering och oförglömliga minnen. Lunch ingår ej men vi stannar vid rekommenderade platser."
    },
    {
      question: "Hur långt i förväg bör jag boka?",
      answer: "Vi rekommenderar att boka minst 2-3 dagar i förväg. Guidade turer är begränsade till 4 förare och fylls snabbt under högsäsong (juni-augusti)."
    },
    {
      question: "Vad händer om vädret är dåligt?",
      answer: "Svenskt väder kan variera. Vi kör i de flesta förhållanden men kan omboka vid extremt väder. Vattentät utrustning finns tillgänglig."
    },
    {
      question: "Kan jag förlänga min hyra?",
      answer: "Ja, beroende på tillgänglighet. Kontakta oss under din hyresperiod för att förlänga. Extra avgifter tillkommer."
    }
  ] : [
    {
      question: "What experience level do I need?",
      answer: "You should have a valid motorcycle license and be comfortable riding on varied terrain. Our routes include gravel roads, so some off-road experience is helpful but not required."
    },
    {
      question: "What's included in the rental?",
      answer: "All rentals include the motorcycle, helmet, and basic insurance. We provide a thorough briefing and route suggestions. Fuel is not included."
    },
    {
      question: "What's included in the guided tour?",
      answer: "The tour includes your motorcycle, all safety gear, an experienced guide, route planning, and incredible memories. Lunch is not included but we stop at recommended spots."
    },
    {
      question: "How far in advance should I book?",
      answer: "We recommend booking at least 2-3 days in advance. Guided tours are limited to 4 riders and fill up quickly during peak season (June-August)."
    },
    {
      question: "What if the weather is bad?",
      answer: "Swedish weather can vary. We ride in most conditions but may reschedule in extreme weather. Waterproof gear is available."
    },
    {
      question: "Can I extend my rental?",
      answer: "Yes, subject to availability. Contact us during your rental period to extend. Additional charges apply."
    }
  ];

  return (
    <section 
      id="faq" 
      className="section-padding grain-texture"
      data-testid="faq-section"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="font-anton text-3xl sm:text-4xl md:text-6xl text-white mb-4">
            {lang === 'sv' ? 'Frågor?' : 'Questions?'}
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-3 sm:space-y-4">
          {faqs.map((faq, i) => (
            <AccordionItem 
              key={i} 
              value={`faq-${i}`}
              className="bg-[#171717] border border-white/5 px-4 sm:px-6"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="font-anton text-base sm:text-lg text-white hover:no-underline py-4 sm:py-6">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="font-manrope text-sm sm:text-base text-white/70 pb-4 sm:pb-6 leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ countryConfig }) => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const lang = getUserLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        country: countryConfig?.id || 'sweden'
      });
      if (response.data.success) {
        setSubmitted(true);
        toast.success(lang === 'sv' ? "Meddelande skickat! Vi återkommer snart." : "Message sent! We'll get back to you soon.");
        setFormData({ name: '', email: '', message: '' });
      }
    } catch (error) {
      toast.error(lang === 'sv' ? "Kunde inte skicka meddelande. Försök igen." : "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get address parts based on country
  const addressLine1 = 'Torpvägen 20';
  const addressLine2 = '147 63 Uttran';
  const phone = SITE_CONFIG.phone;

  return (
    <section 
      id="contact" 
      className="section-padding grain-texture"
      data-testid="contact-section"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-anton text-3xl sm:text-4xl md:text-6xl text-white mb-3 sm:mb-4">
            {lang === 'sv' ? 'Kontakta Oss' : 'Get In Touch'}
          </h2>
          <p className="font-manrope text-white/70 text-base sm:text-lg leading-relaxed">
            {lang === 'sv' ? 'Har du frågor? Vi hjälper gärna till.' : "Have questions? We'd love to hear from you."}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
          {/* Contact Info */}
          <div className="space-y-5 sm:space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#EA580C]/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-[#EA580C]" />
              </div>
              <div>
                <h3 className="font-anton text-lg text-white mb-1">{lang === 'sv' ? 'Plats' : 'Location'}</h3>
                <p className="text-white/70">{addressLine1}</p>
                <p className="text-white/70">{addressLine2}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#EA580C]/20 flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-[#EA580C]" />
              </div>
              <div>
                <h3 className="font-anton text-lg text-white mb-1">{lang === 'sv' ? 'Telefon' : 'Phone'}</h3>
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-white/70 hover:text-[#EA580C] transition-colors">
                  {phone}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#EA580C]/20 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-[#EA580C]" />
              </div>
              <div>
                <h3 className="font-anton text-lg text-white mb-1">Email</h3>
                <a href="mailto:info@arcticriders.com" className="text-white/70 hover:text-[#EA580C] transition-colors">
                  info@arcticriders.com
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-white/70 text-sm">{lang === 'sv' ? 'Namn' : 'Name'}</Label>
              <Input
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-[#171717] border-white/10 text-white mt-1 focus:border-[#EA580C]"
                placeholder={lang === 'sv' ? 'Ditt namn' : 'Your name'}
                data-testid="contact-name"
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-white/70 text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-[#171717] border-white/10 text-white mt-1 focus:border-[#EA580C]"
                placeholder={lang === 'sv' ? 'din@email.se' : 'your@email.com'}
                data-testid="contact-email"
              />
            </div>
            
            <div>
              <Label htmlFor="message" className="text-white/70 text-sm">{lang === 'sv' ? 'Meddelande' : 'Message'}</Label>
              <Textarea
                id="message"
                required
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="bg-[#171717] border-white/10 text-white mt-1 focus:border-[#EA580C] resize-none"
                placeholder={lang === 'sv' ? 'Ditt meddelande...' : 'Your message...'}
                data-testid="contact-message"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white font-anton uppercase tracking-wider py-6"
              data-testid="contact-submit"
            >
              {loading ? (
                lang === 'sv' ? "Skickar..." : "Sending..."
              ) : (
                <>
                  {lang === 'sv' ? 'Skicka Meddelande' : 'Send Message'}
                  <Send className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Booking Modal
const BookingModal = ({ isOpen, onClose, selectedPackage, countryConfig }) => {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [riders, setRiders] = useState(1);
  const [days, setDays] = useState(1);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [timeSlot, setTimeSlot] = useState(null); // "morning" or "afternoon" for half_day
  // Driver's license upload state
  const [licenseFile, setLicenseFile] = useState(null);
  const [licensePreview, setLicensePreview] = useState(null);
  const [licenseUploadId, setLicenseUploadId] = useState(null);
  const [licenseUploadProgress, setLicenseUploadProgress] = useState(0);
  const [licenseUploadStatus, setLicenseUploadStatus] = useState('idle'); // idle | uploading | complete | error
  const [licenseUploadError, setLicenseUploadError] = useState('');
  
  const lang = getUserLanguage();
  const packages = countryConfig?.packages || SITE_CONFIG.packages;
  const currency = countryConfig?.currency || 'kr';
  const currencyCode = countryConfig?.currencyCode || 'SEK';

  const pkg = packages[selectedPackage];
  const isMultiDay = pkg?.multiDay;
  const isGuidedTour = selectedPackage === "guided_tour";
  const isHalfDay = selectedPackage === "half_day";

  // Fetch blocked dates
  useEffect(() => {
    if (isOpen) {
      const fetchBlocked = async () => {
        try {
          const res = await axios.get(`${API}/blocked-dates?country=sweden`);
          setBlockedDates(res.data.blocked_dates || []);
        } catch (e) {}
      };
      fetchBlocked();
    }
  }, [isOpen]);
  
  // Date restrictions — no past dates, respect blocked dates
  const getDisabledDates = (dateToCheck) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dateToCheck < today) return true;
    
    const checkStr = dateToCheck.toISOString().slice(0, 10);
    for (const bd of blockedDates) {
      if (checkStr >= bd.start_date && checkStr <= bd.end_date) return true;
    }
    return false;
  };

  // Format currency (SEK)
  const formatPrice = (amount) => `${amount.toLocaleString('sv-SE')}kr`;

  // Calculate pricing with discount
  const calculatePrice = () => {
    if (!pkg) return { subtotal: 0, discount: 0, total: 0 };
    
    const basePrice = pkg.price;
    const subtotal = basePrice * riders * (isMultiDay ? days : 1);
    
    let discount = 0;
    if (isMultiDay && days >= pkg.discountThreshold) {
      discount = Math.round(subtotal * (pkg.discountPercent / 100));
    }
    
    return {
      subtotal,
      discount,
      total: subtotal - discount,
      hasDiscount: discount > 0
    };
  };

  const pricing = calculatePrice();

  const checkAvailability = useCallback(async (selectedDate, slot) => {
    if (!selectedDate || !selectedPackage) return;
    
    try {
      const response = await axios.post(`${API}/availability`, {
        package_id: selectedPackage,
        date: selectedDate.toISOString(),
        days: isMultiDay ? days : 1,
        riders: riders,
        time_slot: slot || timeSlot || null
      });
      setAvailability(response.data);
    } catch (e) {
      console.error("Failed to check availability");
    }
  }, [selectedPackage, days, riders, isMultiDay, timeSlot]);

  useEffect(() => {
    if (date) {
      checkAvailability(date);
    }
  }, [date, days, riders, checkAvailability]);

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    setCalendarOpen(false);
    if (isHalfDay) setTimeSlot(null); // Reset time slot when date changes
  };

  const handleTimeSlotSelect = (slot) => {
    setTimeSlot(slot);
    if (date) checkAvailability(date, slot);
  };

  const handleSubmit = async () => {
    if (!date || !customerName || !customerEmail || !customerPhone) {
      toast.error(lang === 'sv' ? "Vänligen fyll i alla obligatoriska fält" : "Please fill in all required fields");
      return;
    }
    
    if (isHalfDay && !timeSlot) {
      toast.error(lang === 'sv' ? "Vänligen välj tidslucka" : "Please select a time slot");
      return;
    }
    
    if (!policyAccepted) {
      toast.error(lang === 'sv' ? "Du måste godkänna villkoren för att fortsätta" : "You must accept the rental policy to proceed");
      return;
    }

    if (!licenseUploadId || licenseUploadStatus !== 'complete') {
      toast.error(lang === 'sv' ? "Du måste ladda upp en bild på ditt körkort" : "You must upload a photo of your driver's license");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/booking`, {
        package_id: selectedPackage,
        date: date.toISOString(),
        riders,
        days: isMultiDay ? days : 1,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        special_requests: specialRequests,
        policy_accepted: policyAccepted,
        country: 'sweden',
        currency: 'SEK',
        time_slot: isHalfDay ? timeSlot : null,
        license_file_id: licenseUploadId,
        lang: lang || 'sv'
      }, {
        headers: { origin: window.location.origin }
      });

      // Track booking completion before redirect
      trackEvent('booking_completed', `/`);
      
      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (e) {
      const errorMsg = e.response?.data?.detail || (lang === 'sv' ? "Bokningen misslyckades. Försök igen." : "Booking failed. Please try again.");
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  // Chunked license image upload
  const CHUNK_SIZE = 256 * 1024; // 256 KB
  const uploadLicenseFile = async (file) => {
    setLicenseUploadError('');
    setLicenseUploadProgress(0);
    setLicenseUploadStatus('uploading');
    const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || '');
    if (!isImage) {
      setLicenseUploadStatus('error');
      setLicenseUploadError(lang === 'sv' ? 'Endast bildfiler är tillåtna (JPG, PNG, WEBP, HEIC)' : 'Only image files are allowed (JPG, PNG, WEBP, HEIC)');
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setLicenseUploadStatus('error');
      setLicenseUploadError(lang === 'sv' ? 'Filen är för stor (max 12 MB)' : 'File too large (max 12 MB)');
      return;
    }
    const uploadId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `up-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        // Read as base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result || '';
            const idx = result.indexOf(',');
            resolve(idx >= 0 ? result.slice(idx + 1) : result);
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        await axios.post(`${API}/upload/license/chunk`, {
          upload_id: uploadId,
          chunk_index: i,
          total_chunks: totalChunks,
          file_name: file.name,
          mime_type: file.type || 'image/jpeg',
          data: base64
        });
        setLicenseUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      setLicenseUploadId(uploadId);
      setLicenseUploadStatus('complete');
    } catch (err) {
      setLicenseUploadStatus('error');
      setLicenseUploadError(err.response?.data?.detail || (lang === 'sv' ? 'Uppladdning misslyckades — försök igen' : 'Upload failed — please try again'));
    }
  };

  const handleLicenseSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLicenseFile(file);
    // Preview
    if (licensePreview) URL.revokeObjectURL(licensePreview);
    setLicensePreview(URL.createObjectURL(file));
    uploadLicenseFile(file);
  };

  const removeLicense = () => {
    if (licensePreview) URL.revokeObjectURL(licensePreview);
    setLicenseFile(null);
    setLicensePreview(null);
    setLicenseUploadId(null);
    setLicenseUploadProgress(0);
    setLicenseUploadStatus('idle');
    setLicenseUploadError('');
  };


  // Disable dates based on Sweden season (from June 1)
  const disabledDays = (dateToCheck) => getDisabledDates(dateToCheck);

  if (!isOpen || !pkg) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="booking-modal"
    >
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm" 
        onClick={onClose}
        style={{ pointerEvents: 'auto' }}
      />
      
      <div className="relative bg-[#171717] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#171717] border-b border-white/10 p-6 flex items-center justify-between">
          <div>
            <h3 className="font-anton text-2xl text-white">
              {(lang === 'sv' && pkg.nameSv) ? pkg.nameSv : pkg.name}
            </h3>
            <p className="text-white/60 text-sm">
              {formatPrice(pkg.price)} per {isMultiDay ? (lang === 'sv' ? "dag" : "day") : (lang === 'sv' ? "förare" : "rider")}
              {isMultiDay && <span className="text-green-400 ml-2">• 10% {lang === 'sv' ? 'rabatt 3+ dagar' : 'off 3+ days'}</span>}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="text-white/50 hover:text-white"
            data-testid="close-modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 pt-6">
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s}
                className={`flex-1 h-1 ${s <= step ? "bg-[#EA580C]" : "bg-white/10"}`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 pt-0">
          {/* Step 1: Date & Riders */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="text-white mb-2 block">
                  {isMultiDay ? "Start Date" : "Select Date"}
                </Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-[#0A0A0A] border-white/10 text-white hover:bg-[#0A0A0A] hover:text-white h-12"
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#171717] border-white/10 z-[100]" align="start" sideOffset={5}>
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      disabled={disabledDays}
                      initialFocus
                      className="bg-[#171717] text-white pointer-events-auto"
                      data-testid="calendar"
                    />
                  </PopoverContent>
                </Popover>
                {availability && date && (
                  <p className={`text-sm mt-2 ${availability.available ? "text-green-400" : "text-red-400"}`}>
                    {availability.message}
                    {availability.spots_remaining !== null && availability.spots_remaining !== undefined && (
                      <span> — {availability.spots_remaining} spots left</span>
                    )}
                  </p>
                )}
              </div>

              {/* Time Slot selector for half-day rentals */}
              {isHalfDay && date && (
                <div>
                  <Label className="text-white mb-2 block">
                    {lang === 'sv' ? 'Välj Tidslucka' : 'Select Time Slot'}
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleTimeSlotSelect("morning")}
                      className={`p-4 border text-left transition-all ${
                        timeSlot === "morning" 
                          ? "border-[#EA580C] bg-[#EA580C]/10" 
                          : "border-white/10 bg-[#0A0A0A] hover:border-white/30"
                      }`}
                      data-testid="timeslot-morning"
                    >
                      <div className="font-anton text-base text-white">09:00 – 14:00</div>
                      <div className="text-white/50 text-xs font-manrope mt-1">{lang === 'sv' ? 'Förmiddag' : 'Morning'}</div>
                    </button>
                    <button
                      onClick={() => handleTimeSlotSelect("afternoon")}
                      className={`p-4 border text-left transition-all ${
                        timeSlot === "afternoon" 
                          ? "border-[#EA580C] bg-[#EA580C]/10" 
                          : "border-white/10 bg-[#0A0A0A] hover:border-white/30"
                      }`}
                      data-testid="timeslot-afternoon"
                    >
                      <div className="font-anton text-base text-white">14:00 – 19:00</div>
                      <div className="text-white/50 text-xs font-manrope mt-1">{lang === 'sv' ? 'Eftermiddag' : 'Afternoon'}</div>
                    </button>
                  </div>
                </div>
              )}

              {/* Days selector for multi-day rentals */}
              {isMultiDay && (
                <div>
                  <Label className="text-white mb-2 block">Number of Days</Label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setDays(Math.max(1, days - 1))}
                      className="w-12 h-12 bg-[#0A0A0A] border border-white/10 text-white hover:bg-white/10"
                      data-testid="days-decrease"
                    >
                      -
                    </button>
                    <span className="font-anton text-2xl text-white w-12 text-center">{days}</span>
                    <button
                      onClick={() => setDays(Math.min(14, days + 1))}
                      className="w-12 h-12 bg-[#0A0A0A] border border-white/10 text-white hover:bg-white/10"
                      data-testid="days-increase"
                    >
                      +
                    </button>
                  </div>
                  {days >= 3 && (
                    <p className="text-green-400 text-sm mt-2">10% multi-day discount applied!</p>
                  )}
                  {date && days > 1 && (
                    <p className="text-white/50 text-sm mt-1">
                      {format(date, "MMM d")} → {format(new Date(date.getTime() + (days - 1) * 24 * 60 * 60 * 1000), "MMM d, yyyy")}
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="text-white mb-2 block">Number of Riders</Label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setRiders(Math.max(1, riders - 1))}
                    className="w-12 h-12 bg-[#0A0A0A] border border-white/10 text-white hover:bg-white/10"
                    data-testid="riders-decrease"
                  >
                    -
                  </button>
                  <span className="font-anton text-2xl text-white w-12 text-center">{riders}</span>
                  <button
                    onClick={() => setRiders(Math.min(isGuidedTour ? 4 : 4, riders + 1))}
                    className="w-12 h-12 bg-[#0A0A0A] border border-white/10 text-white hover:bg-white/10"
                    data-testid="riders-increase"
                  >
                    +
                  </button>
                </div>
                {isGuidedTour && (
                  <p className="text-white/50 text-sm mt-2">Maximum 4 riders per tour</p>
                )}
              </div>

              {/* Pricing breakdown */}
              <div className="bg-[#0A0A0A] p-4 border border-white/10 space-y-2">
                {isMultiDay && days > 1 && (
                  <div className="flex justify-between items-center text-white/60 text-sm">
                    <span>{formatPrice(pkg.price)} × {days} {lang === 'sv' ? 'dagar' : 'days'} × {riders} {lang === 'sv' ? (riders > 1 ? 'förare' : 'förare') : (riders > 1 ? 'riders' : 'rider')}</span>
                    <span>{formatPrice(pricing.subtotal)}</span>
                  </div>
                )}
                {pricing.hasDiscount && (
                  <div className="flex justify-between items-center text-green-400 text-sm">
                    <span>10% {lang === 'sv' ? 'Flerdagsrabatt' : 'Multi-day Discount'}</span>
                    <span>-{formatPrice(pricing.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-white/10">
                  <span className="text-white/60">Total</span>
                  <div className="text-right">
                    {pricing.hasDiscount && (
                      <span className="text-white/40 line-through text-sm mr-2">{formatPrice(pricing.subtotal)}</span>
                    )}
                    <span className="font-anton text-2xl text-[#EA580C]">{formatPrice(pricing.total)}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => { setStep(2); trackBookingStep(2); }}
                disabled={!date || (availability && !availability.available) || (isHalfDay && !timeSlot)}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="step1-continue"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Customer Details */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <Label className="text-white mb-2 block">{lang === 'sv' ? 'För- och efternamn *' : 'Full Name *'}</Label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white h-12"
                  placeholder={lang === 'sv' ? 'Ditt namn' : 'Your name'}
                  data-testid="customer-name"
                />
              </div>

              <div>
                <Label className="text-white mb-2 block">{lang === 'sv' ? 'E-post *' : 'Email *'}</Label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white h-12"
                  placeholder="din@email.se"
                  data-testid="customer-email"
                />
              </div>

              <div>
                <Label className="text-white mb-2 block">{lang === 'sv' ? 'Telefon *' : 'Phone *'}</Label>
                <Input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white h-12"
                  placeholder="+46..."
                  data-testid="customer-phone"
                />
              </div>

              <div>
                <Label className="text-white mb-2 block">{lang === 'sv' ? 'Övriga önskemål' : 'Special Requests'}</Label>
                <textarea
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white p-3 min-h-[100px] font-manrope"
                  placeholder={lang === 'sv' ? 'Eventuella önskemål eller noteringar...' : 'Any special requests or notes...'}
                  data-testid="special-requests"
                />
              </div>

              {/* Driver's License upload */}
              <div className="bg-[#0A0A0A] border border-white/10 p-4">
                <Label className="text-white mb-2 block">
                  {lang === 'sv' ? 'Körkort (bild) *' : "Driver's License (photo) *"}
                </Label>
                <p className="text-white/50 text-xs mb-3 font-manrope">
                  {lang === 'sv'
                    ? 'Ladda upp en tydlig bild på ditt MC-körkort (framsida). Bilden lagras säkert och delas endast med vårt team.'
                    : 'Upload a clear photo of your motorcycle license (front). The image is stored securely and only shared with our team.'}
                </p>
                {!licensePreview ? (
                  <label
                    htmlFor="license-input"
                    className="block w-full border border-dashed border-white/20 hover:border-[#EA580C] transition-colors p-6 text-center cursor-pointer"
                    data-testid="license-upload-trigger"
                  >
                    <div className="text-white/70 font-manrope text-sm">
                      {lang === 'sv' ? 'Klicka för att välja en bild (JPG, PNG, HEIC, max 12 MB)' : 'Click to choose an image (JPG, PNG, HEIC, max 12 MB)'}
                    </div>
                    <input
                      id="license-input"
                      type="file"
                      accept="image/*,.heic,.heif"
                      onChange={handleLicenseSelect}
                      className="hidden"
                      data-testid="license-file-input"
                    />
                  </label>
                ) : (
                  <div className="flex items-start gap-4">
                    <img
                      src={licensePreview}
                      alt="License preview"
                      className="w-24 h-24 object-cover border border-white/20"
                      data-testid="license-preview"
                    />
                    <div className="flex-1">
                      <p className="text-white text-sm font-manrope truncate" data-testid="license-filename">
                        {licenseFile?.name}
                      </p>
                      {licenseUploadStatus === 'uploading' && (
                        <div className="mt-2" data-testid="license-progress">
                          <div className="h-1 bg-white/10 w-full">
                            <div className="h-1 bg-[#EA580C] transition-all" style={{ width: `${licenseUploadProgress}%` }} />
                          </div>
                          <p className="text-white/60 text-xs mt-1">
                            {lang === 'sv' ? `Laddar upp... ${licenseUploadProgress}%` : `Uploading... ${licenseUploadProgress}%`}
                          </p>
                        </div>
                      )}
                      {licenseUploadStatus === 'complete' && (
                        <p className="text-green-400 text-xs mt-2 flex items-center gap-1" data-testid="license-complete">
                          <Check className="w-4 h-4" />
                          {lang === 'sv' ? 'Uppladdat' : 'Uploaded'}
                        </p>
                      )}
                      {licenseUploadStatus === 'error' && (
                        <p className="text-red-400 text-xs mt-2" data-testid="license-error">
                          {licenseUploadError}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={removeLicense}
                        className="text-white/50 hover:text-white text-xs mt-2 underline"
                        data-testid="license-remove"
                      >
                        {lang === 'sv' ? 'Ta bort & välj annan' : 'Remove & choose another'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="btn-secondary flex-1"
                  data-testid="step2-back"
                >
                  {lang === 'sv' ? 'Tillbaka' : 'Back'}
                </button>
                <button
                  onClick={() => { setStep(3); trackBookingStep(3); }}
                  disabled={!customerName || !customerEmail || !customerPhone || licenseUploadStatus !== 'complete'}
                  className="btn-primary flex-1 disabled:opacity-50"
                  data-testid="step2-continue"
                >
                  {lang === 'sv' ? 'Fortsätt' : 'Continue'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Rental Policy */}
          {step === 3 && (
            <div className="space-y-6">
              <h4 className="font-anton text-xl text-white">Rental Agreement & Policy</h4>
              <p className="text-white/60 text-sm">Please read and accept our rental policy before proceeding.</p>
              
              <div className="bg-[#0A0A0A] border border-white/10 p-4 max-h-[300px] overflow-y-auto text-white/70 text-sm font-manrope space-y-4">
                <div>
                  <h5 className="text-white font-semibold mb-2">1. Acknowledgment of Risk</h5>
                  <p>I understand and accept that operating or riding a motorcycle involves inherent risks, including but not limited to serious injury, permanent disability, or death. I acknowledge that all riding is done entirely at my own risk, regardless of road, weather, terrain conditions, or the conduct of others.</p>
                </div>
                
                <div>
                  <h5 className="text-white font-semibold mb-2">2. Release of Liability</h5>
                  <p>I hereby waive, release, and discharge Arctic Riders, its owners, employees, guides, contractors, and affiliates from any and all liability, claims, or causes of action arising out of or related to my participation in the rental or guided tour, whether caused by negligence or otherwise.</p>
                </div>
                
                <div>
                  <h5 className="text-white font-semibold mb-2">3. Damage Responsibility</h5>
                  <p>I agree that I am 100% financially responsible for any and all damage to the rented motorcycle during the rental/tour period. This includes scratches, broken parts, cosmetic damage, mechanical or structural damage due to crashes or misuse, and replacement of parts or professional repair labor. Normal wear and tear is excluded.</p>
                </div>
                
                <div>
                  <h5 className="text-white font-semibold mb-2">4. Responsibility for Theft</h5>
                  <p>I agree that I am fully responsible for the motorcycle while it is in my possession. If the motorcycle is stolen, lost, or otherwise not returned, I agree to pay the full replacement value of the motorcycle as determined by Arctic Riders.</p>
                </div>
                
                <div>
                  <h5 className="text-white font-semibold mb-2">5. Road Use & Legal Compliance</h5>
                  <p>I agree to ride only on legal roads, both paved and marked gravel, where motorcycles are permitted. Off-road riding is strictly forbidden. I will not enter protected or restricted areas. I am liable for any fines, towing fees, or enforcement actions resulting from my actions.</p>
                </div>
                
                <div>
                  <h5 className="text-white font-semibold mb-2">6. Rider Requirements</h5>
                  <p>I confirm that: I hold a valid motorcycle driver's license for the class of vehicle I am renting. I am not under the influence of alcohol, drugs, or any impairing substances. I will wear a helmet and protective gear at all times. I have sufficient riding experience to operate the motorcycle safely in mixed terrain and changing conditions.</p>
                </div>
                
                <div>
                  <h5 className="text-white font-semibold mb-2">7. Indemnity Clause</h5>
                  <p>I agree to indemnify and hold harmless Arctic Riders from any third-party claims, lawsuits, or liabilities arising from my use of the motorcycle, including claims by other riders, road users, or authorities.</p>
                </div>
                
                <div>
                  <h5 className="text-white font-semibold mb-2">8. Jurisdiction</h5>
                  <p>This Agreement is governed by the laws of Sweden. Any disputes shall be resolved exclusively by Swedish courts.</p>
                </div>
              </div>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={policyAccepted}
                  onChange={(e) => setPolicyAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#EA580C]"
                  data-testid="policy-checkbox"
                />
                <span className="text-white/80 text-sm">
                  I have read, understood, and agree to the Rental Agreement & Policy, including the{' '}
                  <a href="/villkor" target="_blank" className="text-[#EA580C] hover:underline">
                    Terms & Conditions, Cancellation Policy, and Insurance Information
                  </a>. I accept full responsibility as outlined above.
                </span>
              </label>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(2)}
                  className="btn-secondary flex-1"
                  data-testid="step3-back"
                >
                  Back
                </button>
                <button
                  onClick={() => { setStep(4); trackBookingStep(4); }}
                  disabled={!policyAccepted}
                  className="btn-primary flex-1 disabled:opacity-50"
                  data-testid="step3-continue"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="space-y-6">
              <h4 className="font-anton text-xl text-white">Booking Summary</h4>
              
              <div className="bg-[#0A0A0A] p-4 border border-white/10 space-y-3">
                <div className="flex justify-between">
                  <span className="text-white/60">Experience</span>
                  <span className="text-white">{pkg.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">{isMultiDay ? "Start Date" : "Date"}</span>
                  <span className="text-white">{date ? format(date, "PPP") : "-"}</span>
                </div>
                {isMultiDay && days > 1 && (
                  <div className="flex justify-between">
                    <span className="text-white/60">End Date</span>
                    <span className="text-white">
                      {date ? format(new Date(date.getTime() + (days - 1) * 24 * 60 * 60 * 1000), "PPP") : "-"}
                    </span>
                  </div>
                )}
                {isMultiDay && (
                  <div className="flex justify-between">
                    <span className="text-white/60">Days</span>
                    <span className="text-white">{days}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-white/60">Riders</span>
                  <span className="text-white">{riders}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Name</span>
                  <span className="text-white">{customerName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Email</span>
                  <span className="text-white">{customerEmail}</span>
                </div>
                {pricing.hasDiscount && (
                  <>
                    <div className="border-t border-white/10 pt-3 mt-3">
                      <div className="flex justify-between text-white/60 text-sm">
                        <span>{lang === 'sv' ? 'Delsumma' : 'Subtotal'}</span>
                        <span>{formatPrice(pricing.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-green-400 text-sm mt-1">
                        <span>10% {lang === 'sv' ? 'Flerdagsrabatt' : 'Multi-day Discount'}</span>
                        <span>-{formatPrice(pricing.discount)}</span>
                      </div>
                    </div>
                  </>
                )}
                <div className={`${pricing.hasDiscount ? "" : "border-t border-white/10"} pt-3 mt-3`}>
                  <div className="flex justify-between items-center">
                    <span className="text-white font-semibold">Total</span>
                    <span className="font-anton text-3xl text-[#EA580C]">{formatPrice(pricing.total)}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(3)}
                  className="btn-secondary flex-1"
                  data-testid="step4-back"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-primary flex-1 disabled:opacity-50"
                  data-testid="confirm-booking"
                >
                  {loading ? "Processing..." : "Pay Now"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Footer
const Footer = () => {
  const lang = getUserLanguage();
  
  const address = 'Torpvägen 20, 147 63 Uttran';
  const phone = SITE_CONFIG.phone;
  const tagline = lang === 'sv' 
    ? 'Upplev Sverige från sadeln på en motorcykel. Äventyr väntar på skogsvägar och kustvägar.'
    : 'Experience Sweden from the seat of a motorcycle. Adventure awaits on forest roads and coastal routes.';
  const companyDesc = lang === 'sv' ? 'Motorcykeluthyrning i Stockholm' : 'Motorcycle rental company in Stockholm';

  return (
    <footer 
      className="bg-[#0A0A0A] border-t border-white/10 py-10 sm:py-16 px-5 sm:px-6 md:px-12"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-8 sm:mb-12">
          <div>
            <img src={IMAGES.logoVertical} alt="Arctic Riders" className="h-16 sm:h-20 mb-4 sm:mb-6" />
            <p className="font-manrope text-white/60 text-xs sm:text-sm leading-relaxed">
              {tagline}
            </p>
          </div>
          
          <div>
            <h4 className="font-anton text-xl text-white mb-6">{lang === 'sv' ? 'Kontakt' : 'Contact'}</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-white/70">
                <Mail className="w-5 h-5 text-[#EA580C]" />
                <a href="mailto:info@arcticriders.com" className="hover:text-white transition-colors">
                  info@arcticriders.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <Phone className="w-5 h-5 text-[#EA580C]" />
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                  {phone}
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/70">
                <MapPin className="w-5 h-5 text-[#EA580C] flex-shrink-0 mt-0.5" />
                <span>{address}</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-anton text-xl text-white mb-6">{lang === 'sv' ? 'Snabblänkar' : 'Quick Links'}</h4>
            <ul className="space-y-3">
              {(lang === 'sv' 
                ? ["Äventyr", "Tur", "Motorcyklar", "Omdömen", "FAQ"]
                : ["Adventures", "Tour", "Bikes", "Reviews", "FAQ"]
              ).map((item, idx) => {
                const hrefs = ["adventures", "tour", "bikes", "reviews", "faq"];
                return (
                  <li key={item}>
                    <a 
                      href={`#${hrefs[idx]}`} 
                      className="text-white/70 hover:text-white transition-colors font-manrope"
                    >
                      {item}
                    </a>
                  </li>
                );
              })}
              <li>
                <a 
                  href="/artiklar" 
                  className="text-white/70 hover:text-white transition-colors font-manrope"
                >
                  {lang === 'sv' ? 'Artiklar' : 'Articles'}
                </a>
              </li>
              <li>
                <Link 
                  to="/villkor" 
                  className="text-white/70 hover:text-white transition-colors font-manrope"
                >
                  {lang === 'sv' ? 'Villkor' : 'Terms & Conditions'}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 sm:pt-8 flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
          <p className="text-white/40 text-xs sm:text-sm font-manrope">
            © {new Date().getFullYear()} Arctic Riders · Org.nr 559205-3432. {lang === 'sv' ? 'Alla rättigheter förbehållna.' : 'All rights reserved.'}
          </p>
          <p className="text-white/40 text-xs sm:text-sm font-manrope">
            {companyDesc}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Success/Cancel Handler
const PaymentResult = () => {
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const success = params.get("booking_success");
    const cancelled = params.get("booking_cancelled");

    if (cancelled) {
      setStatus("cancelled");
      setChecking(false);
      return;
    }

    if (success && sessionId) {
      // Poll for payment status
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) {
          setStatus("pending");
          setChecking(false);
          return;
        }

        try {
          const response = await axios.get(`${API}/payment/status/${sessionId}`);
          if (response.data.payment_status === "paid") {
            setStatus("success");
            setChecking(false);
          } else if (response.data.status === "expired") {
            setStatus("expired");
            setChecking(false);
          } else {
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch (e) {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      };

      pollStatus();
    } else {
      setChecking(false);
    }
  }, []);

  if (checking) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#EA580C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white font-manrope">Confirming your booking...</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div 
        className="fixed inset-0 z-50 bg-[#0A0A0A] flex items-center justify-center p-6"
        data-testid="booking-success"
      >
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="font-anton text-4xl text-white mb-4">Booking Confirmed!</h2>
          <p className="text-white/70 font-manrope mb-8">
            Thank you for your booking. You will receive a confirmation email shortly 
            with all the details for your adventure.
          </p>
          <a href="/" className="btn-primary inline-block">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  if (status === "cancelled") {
    return (
      <div 
        className="fixed inset-0 z-50 bg-[#0A0A0A] flex items-center justify-center p-6"
        data-testid="booking-cancelled"
      >
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="font-anton text-4xl text-white mb-4">Booking Cancelled</h2>
          <p className="text-white/70 font-manrope mb-8">
            Your booking was cancelled. No charges were made to your card.
          </p>
          <a href="/" className="btn-primary inline-block">
            Try Again
          </a>
        </div>
      </div>
    );
  }

  return null;
};

// Upcoming Tours Section (Sweden only)
const UpcomingToursSection = () => {
  const [tours, setTours] = useState([]);
  const lang = getUserLanguage();

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const res = await axios.get(`${API}/tour-products?status=published&country=sweden`);
        setTours(res.data.tours || []);
      } catch (e) {}
    };
    fetchTours();
  }, []);

  if (tours.length === 0) return null;

  const diffColor = { easy: "text-green-400 bg-green-400/10", medium: "text-yellow-400 bg-yellow-400/10", hard: "text-red-400 bg-red-400/10" };
  const diffLabel = { easy: lang === 'sv' ? "Enkel" : "Easy", medium: lang === 'sv' ? "Medel" : "Medium", hard: lang === 'sv' ? "Svår" : "Hard" };
  const formatTourPrice = (price) => `${Number(price).toLocaleString('sv-SE')} kr`;

  return (
    <section id="upcoming-tours" className="section-padding bg-[#171717] grain-texture" data-testid="upcoming-tours-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="font-anton text-3xl sm:text-4xl md:text-6xl text-white mb-3 sm:mb-4">
            {lang === 'sv' ? 'Kommande Turer' : 'Upcoming Tours'}
          </h2>
          <p className="font-manrope text-sm sm:text-base text-white/60 max-w-xl mx-auto leading-relaxed">
            {lang === 'sv' ? 'Unika guidade motorcykelturer med begränsat antal platser. Boka din plats nu!' : 'Unique guided motorcycle tours with limited spots. Book your spot now!'}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {tours.map((tour) => {
            const spotsLeft = (tour.max_riders || 4) - (tour.booked_riders || 0);
            const tourDate = tour.date ? new Date(tour.date) : null;
            const tourEnd = tour.end_date ? new Date(tour.end_date) : null;
            let dateStr = '';
            if (tourDate) {
              const opts = { month: 'short', day: 'numeric' };
              const loc = lang === 'sv' ? 'sv-SE' : 'en-US';
              if (tourEnd) {
                // multi-day: "27-28 jun"
                const sameMonth = tourDate.getMonth() === tourEnd.getMonth();
                dateStr = sameMonth
                  ? `${tourDate.getDate()}-${tourEnd.toLocaleDateString(loc, opts)}`
                  : `${tourDate.toLocaleDateString(loc, opts)} – ${tourEnd.toLocaleDateString(loc, opts)}`;
              } else {
                dateStr = tourDate.toLocaleDateString(loc, opts);
              }
            }
            const isMultiDay = !!tourEnd;
            const title = lang === 'sv' && tour.title_sv ? tour.title_sv : tour.title;

            return (
              <a
                key={tour.id}
                href={`/turer/${tour.slug}`}
                className="group block bg-[#0A0A0A] border border-white/5 overflow-hidden hover:border-[#EA580C]/40 transition-all duration-300"
                data-testid={`tour-card-${tour.slug}`}
              >
                {tour.image && (
                  <div className="relative h-44 sm:h-48 overflow-hidden">
                    <img src={resolveImageUrl(tour.image)} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="bg-[#0A0A0A]/80 backdrop-blur-sm text-white font-anton text-sm px-3 py-1">{dateStr}</span>
                      {tour.difficulty && <span className={`text-xs uppercase font-bold px-2 py-1 ${diffColor[tour.difficulty]}`}>{diffLabel[tour.difficulty]}</span>}
                    </div>
                    {spotsLeft <= 0 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="font-anton text-xl text-red-400">{lang === 'sv' ? 'FULLBOKAD' : 'SOLD OUT'}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="p-4 sm:p-5">
                  <h3 className="font-anton text-lg sm:text-xl text-white mb-2 group-hover:text-[#EA580C] transition-colors">{title}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-white/50 font-manrope mb-3">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{isMultiDay ? (lang === 'sv' ? '2 dagar' : '2 days') : `${tour.time_start}–${tour.time_end}`}</span>
                    {tour.distance && <span className="flex items-center gap-1"><Mountain className="w-3.5 h-3.5" />{tour.distance}</span>}
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{spotsLeft > 0 ? `${spotsLeft} ${lang === 'sv' ? 'platser kvar' : 'left'}` : (lang === 'sv' ? 'Fullbokad' : 'Full')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-anton text-xl text-[#EA580C]">{formatTourPrice(tour.price)}</span>
                    <span className="text-[#EA580C] font-manrope text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      {lang === 'sv' ? 'Se mer' : 'View'} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Main App
function MainSite() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [lang, setLang] = useState(getUserLanguage());

  // Listen for language changes
  useEffect(() => {
    const handleLangChange = () => setLang(getUserLanguage());
    window.addEventListener('langchange', handleLangChange);
    return () => window.removeEventListener('langchange', handleLangChange);
  }, []);

  // Track page view on mount
  useEffect(() => {
    trackEvent('page_view', '/');
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openBooking = (packageId) => {
    setSelectedPackage(packageId);
    setBookingOpen(true);
    trackBookingStarted();
  };

  // Check for payment result on load
  const params = new URLSearchParams(window.location.search);
  const hasPaymentResult = params.has("booking_success") || params.has("booking_cancelled");

  return (
    <div className="App dark" key={lang}>
      <Helmet>
        <title>{t(lang, SITE_CONFIG.seo.title, SITE_CONFIG.seo.titleEn)}</title>
        <meta name="description" content={t(lang, SITE_CONFIG.seo.description, SITE_CONFIG.seo.descriptionEn)} />
      </Helmet>
      <Toaster position="top-center" theme="dark" />
      
      {hasPaymentResult && <PaymentResult />}
      
      <Navigation scrollToSection={scrollToSection} openBooking={openBooking} />
      
      <main>
        <HeroSection scrollToSection={scrollToSection} openBooking={openBooking} />
        <AboutSection />
        <AdventuresSection openBooking={openBooking} />
        <UpcomingToursSection />
        <GuidesSection />
        <BikesSection openBooking={openBooking} countryConfig={SITE_CONFIG} />
        <WhyUsSection countryConfig={SITE_CONFIG} />
        <GallerySection countryConfig={SITE_CONFIG} />
        <FAQSection countryConfig={SITE_CONFIG} />
        <ContactSection countryConfig={SITE_CONFIG} />
      </main>

      <Footer />

      <BookingModal 
        isOpen={bookingOpen} 
        onClose={() => setBookingOpen(false)} 
        selectedPackage={selectedPackage}
        countryConfig={SITE_CONFIG}
      />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Routes>
          {/* Primary Swedish URLs */}
          <Route path="/" element={<MainSite />} />
          <Route path="/turer/:slug" element={<TourProductPage />} />
          <Route path="/artiklar" element={<ArticlesList />} />
          <Route path="/artiklar/:slug" element={<ArticlePage />} />
          <Route path="/villkor" element={<Terms />} />
          <Route path="/tack" element={<ThankYou />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/sign-agreement/:id" element={<SignAgreement />} />

          {/* Legacy URL redirects (SEO-friendly via <Navigate replace>) */}
          <Route path="/sweden" element={<Navigate to="/" replace />} />
          <Route path="/sweden/tours/:slug" element={<LegacyTourRedirect />} />
          <Route path="/tours/:slug" element={<LegacyTourRedirect />} />
          <Route path="/articles" element={<Navigate to="/artiklar" replace />} />
          <Route path="/articles/:slug" element={<LegacyArticleRedirect />} />
          <Route path="/terms" element={<Navigate to="/villkor" replace />} />
          <Route path="/thank-you" element={<Navigate to="/tack" replace />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

// Preserve slug + any query params when redirecting legacy tour URLs
function LegacyTourRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/turer/${slug}${window.location.search}`} replace />;
}
function LegacyArticleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/artiklar/${slug}${window.location.search}`} replace />;
}

export default App;
